import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Delete as DeleteIcon, Warning as WarningIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export interface DeleteConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  productName: string;
  translationCount: number;
  loading?: boolean;
}

export const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  productName,
  translationCount,
  loading = false,
}) => {
  const { t } = useTranslation();

  const handleClose = () => {
    if (loading) return; // Prevent closing while deleting
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { p: 1 },
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: 'error.light',
              color: 'error.contrastText',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <DeleteIcon />
          </Box>
          <Box>
            <Typography variant="h6" color="error">
              {t('productDetails.deleteProductTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('productDetails.deleteSubtitle')}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Stack spacing={2}>
            <Typography variant="body1">
              <strong>{t('productDetails.deleteWarningIntro')}</strong>
            </Typography>
            <Typography variant="body2">{t('productDetails.deleteWarningProduct', { name: productName })}</Typography>
            {translationCount > 0 && (
              <Typography variant="body2">
                {t('productDetails.deleteWarningTranslations', { count: translationCount })}
              </Typography>
            )}
          </Stack>
        </Alert>

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <WarningIcon color="warning" />
          <Typography variant="body2" color="text.secondary">
            {t('productDetails.deleteWarningFooter')}
          </Typography>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {t('productDetails.deleteConfirmQuestion')}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading} startIcon={<CancelIcon />}>
          {t('productDetails.cancel')}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <DeleteIcon />}
        >
          {loading ? t('productDetails.deleting') : t('productDetails.deleteProductTitle')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

